import React, { useState, useMemo } from 'react';
import { 
  Eye, Check, X, User, Mail, Phone, CreditCard, Calendar, 
  Hash, DollarSign, Package, Wallet, TrendingUp, Clock 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupabase } from '../../hooks/useSupabase';
import { aicisAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, formatCurrency, getStatusColor, getStatusLabel } from '../../utils/helpers';

export default function EventRegistrations() {
  const { data, refetch } = useSupabase(aicisAPI.getEventRegistrations);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calculate totals - based on status
  const totals = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalAmount: 0,
        completedAmount: 0,
        pendingAmount: 0,
        rejectedAmount: 0,
        totalCount: 0,
        completedCount: 0,
        pendingCount: 0,
        rejectedCount: 0,
      };
    }

    const totalAmount = data.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    const completedAmount = data
      .filter(item => item.status === 'completed' || item.status === 'completed')
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    const pendingAmount = data
      .filter(item => item.status === 'pending_confirmation' || item.status === 'pending')
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    const rejectedAmount = data
      .filter(item => item.status === 'rejected')
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    const completedCount = data.filter(item => item.status === 'completed' || item.status === 'completed').length;
    const pendingCount = data.filter(item => item.status === 'pending_confirmation' || item.status === 'pending').length;
    const rejectedCount = data.filter(item => item.status === 'rejected').length;

    return {
      totalAmount,
      completedAmount,
      pendingAmount,
      rejectedAmount,
      totalCount: data.length,
      completedCount,
      pendingCount,
      rejectedCount,
    };
  }, [data]);

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try {
      await aicisAPI.updateEventRegistration(id, { status });
      toast.success(`Registration ${status} successfully`);
      refetch();
      setIsModalOpen(false);
      setSelectedRegistration(null);
    } catch (error) {
      toast.error(`Failed to ${status} registration`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleView = (item) => {
    setSelectedRegistration(item);
    setIsModalOpen(true);
  };

  const renderRegistrationDetails = (registration) => {
    if (!registration) return null;

    let itemsList = [];
    try {
      if (registration.items) {
        itemsList = typeof registration.items === 'string' 
          ? JSON.parse(registration.items) 
          : registration.items;
      }
    } catch (e) {
      itemsList = [registration.items];
    }

    const isPending = registration.status === 'pending_confirmation' || registration.status === 'pending';
    const isCompleted = registration.status === 'completed';
    const isRejected = registration.status === 'rejected';

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Transaction ID</p>
              <p className="text-sm font-medium text-gray-900">{registration.transaction_id || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <User className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Customer Name</p>
              <p className="text-sm font-medium text-gray-900">{registration.customer_name || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm font-medium text-gray-900">{registration.customer_email || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm font-medium text-gray-900">{registration.customer_phone || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Amount</p>
              <p className="text-sm font-medium text-gray-900">{formatCurrency(registration.amount)}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <CreditCard className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Payment Method</p>
              <p className="text-sm font-medium text-gray-900">{registration.payment_method || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Hash className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Reference</p>
              <p className="text-sm font-medium text-gray-900">{registration.reference || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Date</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(registration.created_at)}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-400">#</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <span className={`badge ${getStatusColor(registration.status)} mt-1`}>
                {getStatusLabel(registration.status)}
              </span>
            </div>
          </div>
        </div>

        {itemsList && itemsList.length > 0 && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Items Purchased
            </h4>
            <div className="space-y-2">
              {itemsList.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                  <span className="text-sm text-gray-700">{typeof item === 'string' ? item : item.name || item.item || 'Item'}</span>
                  {item.price && (
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(item.price)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          {/* Confirm Payment button - only for pending */}
          {isPending && (
            <button
              onClick={() => handleStatusUpdate(registration.id, 'completed')}
              disabled={processingId === registration.id}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              {processingId === registration.id ? 'Processing...' : '✅ Confirm Payment Received'}
            </button>
          )}

          {/* Undo button - only for completed */}
          {isCompleted && (
            <button
              onClick={() => handleStatusUpdate(registration.id, 'pending_confirmation')}
              disabled={processingId === registration.id}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Wallet className="w-4 h-4" />
              {processingId === registration.id ? 'Processing...' : '↩️ Undo - Mark as Pending'}
            </button>
          )}

          {/* Reject button - only for pending */}
          {isPending && (
            <button
              onClick={() => handleStatusUpdate(registration.id, 'rejected')}
              disabled={processingId === registration.id}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-4 h-4" />
              {processingId === registration.id ? 'Processing...' : 'Reject Registration'}
            </button>
          )}

          {isRejected && (
            <div className="pt-2">
              <p className="text-sm text-gray-500 text-center">
                This registration has been <span className="font-semibold text-red-600">Rejected</span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'transaction_id', label: 'Transaction ID', sortable: true },
    { key: 'customer_name', label: 'Customer Name', sortable: true },
    { key: 'customer_email', label: 'Email', sortable: true },
    { key: 'customer_phone', label: 'Phone' },
    { 
      key: 'amount', 
      label: 'Amount',
      render: (value) => formatCurrency(value)
    },
    { key: 'payment_method', label: 'Method' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Date',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending_confirmation', 'pending', 'completed', 'rejected']
    },
    {
      label: 'Confirm Payment',
      icon: <Check className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'completed'),
      className: 'text-green-600 hover:bg-green-50',
      showWhen: ['pending_confirmation', 'pending'],
      disabled: (item) => processingId === item.id
    },
    {
      label: 'Undo Payment',
      icon: <Wallet className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'pending_confirmation'),
      className: 'text-yellow-600 hover:bg-yellow-50',
      showWhen: ['completed'],
      disabled: (item) => processingId === item.id
    },
    {
      label: 'Reject',
      icon: <X className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'rejected'),
      className: 'text-red-600 hover:bg-red-50',
      showWhen: ['pending_confirmation', 'pending'],
      disabled: (item) => processingId === item.id
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Event Registrations</h1>
        <p className="text-gray-600">Manage event registrations and transactions</p>
        <p className="text-sm text-gray-500 mt-1">
          💡 Check your bank account, then confirm payments received
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalAmount)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{totals.totalCount} total registrations</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">✅ Completed</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.completedAmount)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Wallet className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{totals.completedCount} payments completed</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">⏳ Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(totals.pendingAmount)}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{totals.pendingCount} payments pending</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">❌ Rejected</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.rejectedAmount)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <X className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{totals.rejectedCount} payments rejected</p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} registrations`}
        actions={actions}
        searchPlaceholder="Search registrations..."
        onRowClick={(item) => handleView(item)}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRegistration(null);
        }}
        title="Registration Details"
        size="max-w-3xl"
      >
        {renderRegistrationDetails(selectedRegistration)}
      </Modal>
    </div>
  );
}